import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import { NotificationCenter } from '../../components/common/NotificationCenter/NotificationCenter';
import { useAuth } from '../../contexts/AuthContext';
import { getWishById, contactWish } from '../../services/wishesApi';
import type { Notification } from '../../services/notificationsApi';
import type { ApiWish } from '../../types';
import styles from './MobileWishDetailPage.module.css';

const MobileWishDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [wish, setWish] = useState<ApiWish | null>(null);
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const handleNotificationClick = (notification: Notification) => {
    console.log('Notification clicked:', notification);
  };

  useEffect(() => {
    const fetchWish = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const data = await getWishById(id);
        setWish(data);
      } catch (error) {
        console.error('Failed to fetch wish:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWish();
  }, [id]);

  // Handle countdown timer for redirect
  useEffect(() => {
    if (redirectCountdown === null) return;

    if (redirectCountdown <= 0) {
      navigate('/pricing');
      return;
    }

    const timer = setTimeout(() => {
      setRedirectCountdown(redirectCountdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  const handleContact = async () => {
    if (!id || !isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user has permission to contact (free plan users cannot contact)
    const userPlan = user?.plan || 'free';
    if (userPlan === 'free') {
      setContactMessage('聯絡許願者需要付費方案');
      // Start countdown for redirect
      setRedirectCountdown(3);
      return;
    }

    setContacting(true);
    setContactMessage('');

    try {
      const result = await contactWish(id);
      if (result.success) {
        setContactMessage('聯繫請求已發送！');
      } else {
        setContactMessage(result.message || '聯繫失敗，請稍後再試。');
      }
    } catch {
      setContactMessage('聯繫失敗，請稍後再試。');
    } finally {
      setContacting(false);
    }
  };

  const handleCancelRedirect = () => {
    setRedirectCountdown(null);
    setContactMessage('');
  };

  if (loading) {
    return (
      <div>
        <MobileHeader title="載入中..." onBack={() => navigate('/wishes')} />
        <div className={styles.loading}>載入中...</div>
      </div>
    );
  }

  if (!wish) {
    return (
      <div>
        <MobileHeader title="找不到" onBack={() => navigate('/wishes')} />
        <div className={styles.notFound}>
          <p>找不到此許願</p>
          <button onClick={() => navigate('/wishes')} className={styles.backBtn}>
            返回許願池
          </button>
        </div>
      </div>
    );
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      '維修': '汽車維修',
      '保養': '汽車保養',
      '美容': '汽車美容',
      '改裝': '汽車改裝',
    };
    return labels[category] || category;
  };

  return (
    <div>
      <MobileHeader
        title="許願詳情"
        onBack={() => navigate('/wishes')}
        rightAction={isAuthenticated ? (
          <NotificationCenter onNotificationClick={handleNotificationClick} />
        ) : undefined}
      />

      <div className={styles.container}>
        {/* Title and Status */}
        <div className={styles.header}>
          <h1 className={styles.title}>{wish.title}</h1>
          <div className={styles.meta}>
            <span className={`${styles.status} ${styles[wish.status]}`}>
              {wish.status === 'open' && '尋找中'}
              {wish.status === 'matched' && '配對中'}
              {wish.status === 'closed' && '已結案'}
            </span>
            {wish.urgent && <span className={styles.urgent}>急件</span>}
          </div>
        </div>

        {/* Description */}
        {wish.description && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>需求描述</h2>
            <p className={styles.description}>{wish.description}</p>
          </div>
        )}

        {/* Details */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>詳細資訊</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.label}>類別</span>
              <span>{getCategoryLabel(wish.category)}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>地區</span>
              <span>{wish.region}{wish.district ? ` (${wish.district})` : ''}</span>
            </div>
            {wish.budget && (
              <div className={styles.infoItem}>
                <span className={styles.label}>預算</span>
                <span>NT${wish.budget}</span>
              </div>
            )}
            <div className={styles.infoItem}>
              <span className={styles.label}>發布者</span>
              <span>{wish.userName || '匿名'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>時間</span>
              <span>{new Date(wish.createdAt).toLocaleDateString('zh-TW')}</span>
            </div>
          </div>
        </div>

        {/* Contact Button */}
        {wish.status === 'open' && (
          <div className={styles.contactSection}>
            {contactMessage && (
              <div className={`${styles.message} ${
                contactMessage.includes('成功') ? styles.success : styles.error
              }`}>
                {redirectCountdown !== null ? (
                  <>
                    <p>{contactMessage}</p>
                    <p className={styles.countdownText}>
                      將在 {redirectCountdown} 秒後導向至方案頁面...
                    </p>
                    <button
                      onClick={handleCancelRedirect}
                      className={styles.cancelRedirectBtn}
                    >
                      取消導向
                    </button>
                  </>
                ) : (
                  contactMessage
                )}
              </div>
            )}
            {isAuthenticated ? (
              <button
                onClick={handleContact}
                disabled={contacting || redirectCountdown !== null}
                className={styles.contactBtn}
              >
                {contacting ? '發送中...' : '聯繫許願者'}
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={styles.loginBtn}
              >
                登入以聯繫
              </button>
            )}
          </div>
        )}

        {wish.status !== 'open' && (
          <div className={styles.closedNotice}>
            此許願已結案或配對中
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileWishDetailPage;
